import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  Container, 
  IconButton, 
  InputBase, 
  Paper,
  Chip,
  Drawer,
  List,
  ListItemButton,
  ListItemText
} from '@mui/material';
import { Search as SearchIcon, Close as CloseIcon, Menu as MenuIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import UserMenu from './UserMenu';
import { PRODUCT_CATEGORIES } from '../constants/categories';

// Helper hook for debouncing search
const useDebouncedSearch = (searchTerm: string, delay: number = 500) => {
  const [debouncedTerm, setDebouncedTerm] = useState(searchTerm);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTerm(searchTerm);
    }, delay);
    
    return () => clearTimeout(timer);
  }, [searchTerm, delay]);
  
  return debouncedTerm;
};

interface NavbarProps {
  onSearch?: (query: string) => void;
  searchQuery?: string;
  selectedCategory?: typeof PRODUCT_CATEGORIES[number] | '';
  onSelectCategory?: (category: typeof PRODUCT_CATEGORIES[number] | '') => void;
}

const Navbar: React.FC<NavbarProps> = ({ onSearch, searchQuery = '', selectedCategory = '', onSelectCategory }) => {
  const { authState: { isAuthenticated }, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState(searchQuery);
  const debouncedSearchTerm = useDebouncedSearch(searchTerm);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (onSearch && debouncedSearchTerm !== undefined) {
      onSearch(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, onSearch]);

  // Keep local input in sync if searchQuery is controlled externally
  useEffect(() => {
    setSearchTerm(searchQuery);
  }, [searchQuery]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const openMobileMenu = () => setMobileOpen(true);
  const closeMobileMenu = () => setMobileOpen(false);

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (onSearch && searchTerm.trim()) {
      onSearch(searchTerm.trim());
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    if (onSearch) {
      onSearch('');
    }
  };

  const handleSelectCategory = (cat: typeof PRODUCT_CATEGORIES[number] | '') => {
    if (onSelectCategory) {
      onSelectCategory(cat);
    }
    // Ensure we land on the home product list page
    navigate('/');
  };

  const handleLogoClick = () => {
    // reset filters to show all products
    if (onSearch) {
      onSearch('');
    }
    setSearchTerm('');
    if (onSelectCategory) {
      onSelectCategory('');
    }
    // Ensure we land on the home product list page
    navigate('/');
  };

  const navItems: { text: string; path: string }[] = [];

  const authNavItems = isAdmin ? [
    { text: 'Admin', path: '/admin' }
  ] : [];

  return (
    <AppBar 
      className="header-container" 
      position="fixed" 
      elevation={0}
      sx={{ 
        backgroundColor: 'background.paper',
        color: 'text.primary',
        borderBottom: '1px solid',
        borderColor: 'divider',
        zIndex: (theme) => theme.zIndex.appBar,
      }}
    >
      <Container className="navbar" maxWidth={false}>
        <Toolbar className="topheader" disableGutters>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            flexGrow: 1,
            gap: 2
          }}>
            <Box
              component={RouterLink}
              to="/"
              onClick={handleLogoClick}
              className="logo"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                mr: { xs: 1, md: 4 },
                '&:hover': {
                  opacity: 0.8,
                },
              }}
            >
              <Box
                component="img"
                src="/logoicon.svg"
                alt="Monno"
                sx={{ height: 36, width: 'auto', display: 'block' }}
              />
            </Box>

            {/* Main Navigation (desktop only) */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  sx={{
                    color: 'text.primary',
                    '&.active': {
                      color: 'primary.main',
                      fontWeight: 600,
                    },
                  }}
                >
                  {item.text}
                </Button>
              ))}
              
              {isAuthenticated && authNavItems.map((item) => (
                <Button
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  sx={{
                    color: 'text.primary',
                    '&.active': {
                      color: 'primary.main',
                      fontWeight: 600,
                    },
                  }}
                >
                  {item.text}
                </Button>
              ))}
            </Box>

            {/* Search Bar */}
            <Box 
              className="search-form"
              component="form" 
              onSubmit={handleSearchSubmit}
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                flexGrow: 1,
                maxWidth: { xs: 280, sm: 420, md: 600 },
                ml: { xs: 1, md: 2 },
                mr: { xs: 1, md: 2 }
              }}
              
            >
              <Paper
                component="div"
                sx={{
                  p: { xs: '2px 6px', md: '2px 4px' },
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  borderRadius: { xs: 0, md: 2 },
                  boxShadow: 'none',
                  border: { xs: 'none', md: '1px solid' },
                  borderColor: { xs: 'transparent', md: 'divider' },
                  '&:hover': {
                    borderColor: { md: 'primary.main' },
                  },
                  '&:focus-within': {
                    borderColor: { md: 'primary.main' },
                    boxShadow: { md: '0 0 0 2px rgba(79, 70, 229, 0.2)' },
                  },
                }}
                className="search"
              >
                <IconButton className="searchicon" type="submit" sx={{ p: { xs: '6px', md: '10px' }, display: { xs: 'none', md: 'inline-flex' } }} aria-label="search">
                  <SearchIcon />
                </IconButton>
                <InputBase
                  sx={{ 
                    ml: 1, 
                    flex: 1, 
                    fontSize: { xs: '1rem', md: '0.875rem' }, 
                    fontWeight: { xs: 500, md: 400 }, 
                    color: { xs: '#111', md: 'inherit' },
                    '& input::placeholder': {
                      fontWeight: 500,
                      color: '#111',
                      opacity: 1
                    }
                  }}
                  placeholder="Search"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  inputProps={{ 'aria-label': 'search products' }}
                />
                {searchTerm && (
                  <IconButton 
                    onClick={clearSearch}
                    sx={{ p: { xs: '6px', md: '10px' } }} 
                    aria-label="clear search"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Paper>
            </Box>
          </Box>

          {/* Right side: Hamburger on mobile, auth on desktop */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'flex-end' }}>
            <IconButton aria-label="menu" onClick={openMobileMenu}>
              <MenuIcon />
            </IconButton>
          </Box>

          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end', gap: 2 }}>
            {isAuthenticated ? (
              <UserMenu />
            ) : (
              <>
                <Button 
                  color="inherit" 
                  component={RouterLink} 
                  to="/login"
                  sx={{ ml: 2 }}
                  className="login-btn"
                >
                  Login
                </Button>
                <Button 
                  variant="text" 
                  color="inherit" 
                  component={RouterLink} 
                  to="/register"
                  sx={{ ml: 2 }}
                  className="join-btn"
                >
                  Sign Up
                </Button>
              </>)}
          </Box>
        </Toolbar>

        {/* Mobile Drawer */}
        <Drawer
        className="mobile-drawer"
          anchor="right"
          open={mobileOpen}
          onClose={closeMobileMenu}
          PaperProps={{ className: 'mobile-menu', sx: { width: '80%', maxWidth: 320, zIndex: (theme) => theme.zIndex.appBar + 10 } }}
        >
          <Box className="mobile-menu-container" role="presentation" onClick={closeMobileMenu} onKeyDown={closeMobileMenu} sx={{ p: 2 }}>
            <List>
              <ListItemButton component={RouterLink} to="/">
                <ListItemText primary="Home" />
              </ListItemButton>
              {isAdmin && (
                <ListItemButton component={RouterLink} to="/admin">
                  <ListItemText primary="Admin" />
                </ListItemButton>
              )}
              {isAuthenticated ? (
                <>
                  <ListItemButton component={RouterLink} to="/favorites">
                    <ListItemText primary="Favorites" />
                  </ListItemButton>
                  <ListItemButton component={RouterLink} to="/profile">
                    <ListItemText primary="Profile" />
                  </ListItemButton>
                </>
              ) : (
                <>
                  <ListItemButton component={RouterLink} to="/login">
                    <ListItemText primary="Login" />
                  </ListItemButton>
                  <ListItemButton component={RouterLink} to="/register">
                    <ListItemText primary="Sign Up" />
                  </ListItemButton>
                </>
              )}
            </List>
          </Box>
        </Drawer>

        {/* Category filter bar attached to navbar */}
        <Box
          className="bottomheader"
          sx={{
            display: 'block',
            width: '100%',
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            py: 1,
          }}
        >
          <Box className="cat-container" sx={{ px: 2, display: 'inline-flex' }}>
            <Chip
              className="category-chip"
              label="All"
              onClick={() => handleSelectCategory('')}
              variant={selectedCategory === '' ? 'filled' : 'outlined'}
              color={selectedCategory === '' ? 'primary' : 'default'}
              size="small"
            />
            {PRODUCT_CATEGORIES.map((cat) => (
              <Chip
                className="category"
                key={cat}
                label={cat}
                onClick={() => handleSelectCategory(cat)}
                variant={selectedCategory === cat ? 'filled' : 'outlined'}
                color={selectedCategory === cat ? 'primary' : 'default'}
                size="small"
              />
            ))}
          </Box>
        </Box>
      </Container>
    </AppBar>
  );
};

// Error boundary to catch any errors in the Navbar
class ErrorBoundary extends React.Component<{ children: React.ReactNode }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Navbar Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Fallback UI if the Navbar crashes
      return (
        <AppBar position="fixed">
          <Toolbar>
            <Typography>Navigation Error - Please refresh the page</Typography>
          </Toolbar>
        </AppBar>
      );
    }
    return this.props.children;
  }
}

const EnhancedNavbar: React.FC<NavbarProps> = (props) => (
  <ErrorBoundary>
    <Navbar {...props} />
  </ErrorBoundary>
);

export default EnhancedNavbar;
